// CRM System Type Definitions

// 公司（客户）——字段与后端 CompanyVO 对齐；保留旧名便于渐进替换
export interface Company {
  id: string;
  name: string;
  shortName?: string;        // 简称
  industry: string;          // 行业(改为字符串,匹配后端)
  /** 后端 customerSource */
  customerSource?: string;
  /** 后端 customerLevel */
  customerLevel?: string;
  /** @deprecated 使用 customerSource */
  source?: string;
  /** @deprecated 使用 customerLevel */
  level?: string;
  region: string;
  province?: string;         // 省份
  city?: string;             // 城市
  district?: string;         // 区县
  status: 'active' | 'inactive' | 'potential';
  address: string;
  website: string;
  description: string;
  ownerId?: string;          // 负责人ID
  ownerName?: string;        // 负责人姓名
  /** 辅助人 JSON [{userId,userName}]，与负责人同等权限 */
  assistantTeamJson?: string;
  isPublic?: number;         // 是否公共客户
  /** 后端 createTime */
  createTime?: string;
  /** 后端 updateTime */
  updateTime?: string;
  createdAt: string;
  updatedAt: string;
  lastContact: string;
  tags: string[];
  scale?: string;  // 公司规模
  revenue?: string;  // 年营收
  /** 联系人数（后端列表查询批量填充，详情页不保证） */
  contactCount?: number;
  // 转让记录
  transferHistory?: TransferRecord[];
  // 开票信息
  invoiceInfo?: {
    taxNo: string;           // 税号
    companyAddress: string;   // 开票地址
    companyPhone: string;     // 开票电话
    bankName: string;         // 开户银行
    bankAccount: string;      // 银行账号
    invoiceType: InvoiceType; // 默认发票类型
    remark?: string;          // 备注
  };
}

// 客户转让记录
export interface TransferRecord {
  from: string;        // 原负责人
  to: string;          // 新负责人
  transferredAt: string;  // 转让时间
  transferredBy: string;  // 操作人
}

/** 联系人：跨模块汇总；公司可缺省，至少姓名+电话即可建档 */
export interface Contact {
  id: string;
  companyId?: string;
  companyName?: string;
  name: string;
  department?: string;
  position?: string;
  email?: string;
  phone?: string;
  wechat?: string;
  enterpriseWechat?: string;
  isPrimary?: number;  // 0或1
  decisionLevel?: string;
  status?: string;
  tags?: string;
  ownerId?: string;
  ownerName?: string;
  /** customer=客户 supplier=供应商 partner=合作伙伴 expert=专家库 project_trainee=项目学员 unknown/personal=其它 */
  sourceType: string;
  companyType?: string;
  birthday?: string;
  birthdayReminder?: number;
  followupInterval?: number;
  lastContact?: string;
  expertiseArea?: string;  // 专家专业领域
  qualification?: string;  // 专家资质证书
  createTime?: string;
  updateTime?: string;
  /** 与部分页面/导入逻辑对齐的别名字段 */
  createdAt?: string;
  updatedAt?: string;
  /** 备注（部分列表/详情使用） */
  notes?: string;
}

// 客户（兼容旧版）
export interface Customer {
  id: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  industry: string;
  region: string;
  status: 'active' | 'inactive' | 'potential';
  owner: string;
  ownerId?: string;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
  lastContact: string;
  tags: string[];
}

// 销售机会
export interface Opportunity {
  id: string;
  companyId: string;
  companyName: string;
  contactId?: string;
  contactName?: string;
  /** 多联系人及其商机角色 JSON */
  contactTeamJson?: string;
  name: string;
  /** 商机编号（详情/API 可能返回） */
  code?: string;
  amount: number;
  stage: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  probability: number;
  priority?: 'high' | 'medium' | 'low';
  startDate?: string;
  /** 后端 OpportunityVO.expectedDate */
  expectedDate?: string;
  expectedCloseDate: string;
  /** 归属部门/专业团队 ID（sys_department.id），先选部门再选负责人 */
  deptId?: string;
  /** 归属部门名称（后端按 sys_department 回填） */
  deptName?: string;
  owner: string;
  /** 负责人用户 ID（后端 owner 列） */
  ownerId?: string;
  /** 负责人显示名 */
  ownerName?: string;
  /** 部分接口用 active/closed 等 */
  status?: string;
  /** 创建时间（部分接口为 createTime） */
  createTime?: string;
  updateTime?: string;
  createdAt: string;
  updatedAt: string;
  notes: string;
  remark?: string;
  /** 签单后关联的项目 ID（列表 VO 反查） */
  projectId?: string;
  /** 多负责人 JSON：[{ userId, userName }]，与后端 crm_opportunity.owner_team_json 一致 */
  ownerTeamJson?: string;
  /** 转项目审核状态：converted/reviewing/rejected/cancelled（仅已签单有意义） */
  conversionStatus?: string;
  /** 转项目审核状态中文 */
  conversionStatusText?: string;
  /** 签单提交的合同与方案书文件名 */
  signContractFileName?: string;
  signSolutionFileName?: string;
  /** 签单附件 URL（流程载荷，待审核期间可下载） */
  signContractFileUrl?: string;
  signSolutionFileUrl?: string;
}

// 项目
export interface Project {
  id: string;
  name: string;
  /** 项目分类：training-培训项目 / consulting-管理咨询项目 / other-其他项目 */
  projectType?: 'training' | 'consulting' | 'other' | string;
  code: string;
  companyId: string;
  companyName: string;
  // 联系人和负责人改为数组，支持多选和角色
  contactIds?: { contactId: string; contactName: string; role: string }[];
  owners?: { name: string; role: string }[];
  // 兼容旧字段
  contactId?: string;
  contactName?: string;
  /** 归属部门/专业团队 ID（签单时由商机继承，可在项目编辑页修改） */
  deptId?: string;
  /** 归属部门名称（后端按 sys_department 回填） */
  deptName?: string;
  owner?: string;
  /** 负责人（系统用户 id，可选） */
  ownerId?: string;
  /** 负责人展示名（优先于 owner 中的员工 id） */
  ownerName?: string;
  // 商机阶段：线索->意向->方案->谈判->成交/失败
  /** 项目执行态：签单后多为 active + completed；其它为历史/兼容 */
  status: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost' | 'planning' | 'execution' | 'uat' | 'completed' | 'cancelled' | 'active';
  priority: 'high' | 'medium' | 'low';
  progress: number;
  businessCloseStatus?: 'not_submitted' | 'pending_approval' | 'approved' | 'rejected' | string;
  businessCloseWorkflowInstanceId?: string;
  businessCloseSubmittedBy?: string;
  businessCloseSubmittedByName?: string;
  businessCloseSubmittedAt?: string;
  businessCloseApprovedBy?: string;
  businessCloseApprovedByName?: string;
  businessCloseApprovedAt?: string;
  businessCloseRejectedBy?: string;
  businessCloseRejectedByName?: string;
  businessCloseRejectedAt?: string;
  businessCloseNote?: string;
  financeCloseStatus?: 'open' | 'closed' | string;
  financeClosedBy?: string;
  financeClosedByName?: string;
  financeClosedAt?: string;
  financeCloseNote?: string;
  startDate: string;
  endDate: string;
  budget: number;
  contractValue: number;
  description: string;
  /** 后端 ProjectVO.createTime */
  createTime?: string;
  /** 后端 ProjectVO.updateTime */
  updateTime?: string;
  createdAt: string;
  updatedAt: string;
  // 签约文件（合同、方案书）
  contractDocuments?: ProjectDocument[];   // 合同文件
  proposalDocuments?: ProjectDocument[];   // 方案书文件
  // 签约信息
  signedAt?: string;                       // 签约日期
  contractNo?: string;                    // 合同编号
  /** 商机管道阶段（部分页面将商机以 Project 结构展示时使用） */
  stage?: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  /** 商机签单后关联的项目 ID（用于跳转项目管理） */
  linkedProjectId?: string;
  /** 签单来源商机（crm_project.opportunity_id） */
  opportunityId?: string;
  opportunityName?: string;
  /** 后端项目附件（合同/方案等多版本，含 revision） */
  attachments?: ProjectAttachmentRow[];
  /** 后端项目联系人（ProjectVO.contacts） */
  contacts?: ProjectContactRow[];
  /** 后端项目成员配置（ProjectVO.members） */
  members?: ProjectMemberRow[];
  /** 商机转项目审核状态：converted/reviewing/rejected/cancelled（已签单商机以 Project 结构展示时使用） */
  conversionStatus?: string;
  /** 转项目审核状态中文 */
  conversionStatusText?: string;
  /** 签单提交的合同与方案书文件名（已签单列表展示） */
  signContractFileName?: string;
  signSolutionFileName?: string;
}

/** 与后端 ProjectVO.ProjectMemberVO 对齐 */
export interface ProjectMemberRow {
  id: string;
  userId: string;
  userName: string;
  type: 'employee' | 'expert' | string;
  role?: string;
  workDays?: number;
  dailyRate?: number;
  totalCost?: number;
  status?: string;
}

/** 与后端 ProjectVO.ProjectAttachmentVO 对齐 */
export interface ProjectAttachmentRow {
  id: string;
  type: string;
  milestoneId?: string;
  name: string;
  url: string;
  size?: number;
  format?: string;
  uploaderId?: string;
  uploaderName?: string;
  uploadTime?: string;
  remark?: string;
  revision?: number;
  reviewStatus?: ProjectAttachmentReviewStatus;
  rejectReason?: string;
  kbArticleId?: string;
  kbArticleTitle?: string;
}

export type ProjectAttachmentReviewStatus =
  | 'uploaded'
  | 'pending_task_confirmation'
  | 'pending_stage_review'
  | 'rejected'
  | 'project_only'
  | 'needs_redaction'
  | 'kb_archived';

export interface ProjectContactRow {
  id?: string;
  contactId: string;
  contactName: string;
  role?: string;
  phone?: string;
  email?: string;
  isPrimary?: number;
}

// 项目文档
export interface ProjectDocument {
  id: string;
  name: string;              // 文件名
  url: string;                // 文件URL
  size: number;               // 文件大小（字节）
  type: string;              // 文件类型 (pdf, doc, docx, ppt, pptx等)
  uploadedAt: string;        // 上传时间
  uploadedBy: string;         // 上传人
  description?: string;       // 文档描述
  // 版本管理
  version?: number;           // 版本号
  isCurrent?: boolean;        // 是否为当前版本
  changeReason?: string;     // 变更原因
}

// 文档变更记录
export interface DocumentChangeLog {
  id: string;
  projectId: string;
  documentType: 'contract' | 'quote' | 'proposal';
  documentName: string;
  action: 'upload' | 'update' | 'delete';
  operator: string;
  operatorId: string;
  timestamp: string;
  reason?: string;
  version?: number;
}

// 任务附件
export interface TaskAttachment {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  /** 上传时间（详情/列表展示） */
  uploadedAt?: string;
  /** 项目签约附件版本序号（与 crm_project_attachment.revision 一致） */
  revision?: number;
  reviewStatus?: ProjectAttachmentReviewStatus;
  rejectReason?: string;
  kbArticleId?: string;
  kbArticleTitle?: string;
}

// 任务人
export interface TaskUser {
  id: string;
  name: string;
}

// 任务金额类型
export type TaskAmountType = 'service' | 'tax' | 'transport' | 'accommodation' | 'copyright' | 'transfer' | 'other';

// 子任务（任务人支持）
export interface SubTask {
  id: string;
  userId: string;        // 任务人ID
  userName: string;    // 任务人姓名
  roleType: string;    // 角色类型
  roleTypeName: string; // 角色类型名称
  description?: string; // 该任务人的任务描述
  startDate: string;   // 开始日期
  endDate: string;     // 结束日期
  status: 'pending' | 'in_progress' | 'completed'; // 子任务状态
  /** 该任务人分摊金额（合计不得超过父任务 amount） */
  amount?: number;
}

// 任务类型（与种子/后端 crm_task.type、taskLabels 对齐）
export type TaskType =
  | 'development'
  | 'training'
  | 'assessment'
  | 'workshop'
  | 'interview'
  | 'consulting'
  | 'online'
  | 'teaching_assistant'
  | 'visual_recording'
  | 'call'
  | 'email'
  | 'meeting'
  | 'visit'
  | 'other';

export type TaskWorkMode = 'onsite' | 'backoffice' | 'remote' | 'hybrid';

// 任务
export interface Task {
  id: string;
  title: string;
  description: string;
  type: TaskType;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  priority: 'low' | 'medium' | 'high';
  dueDate: string;
  // 新增字段
  startDate?: string;  // 开始时间
  endDate?: string;    // 结束时间
  days?: number;       // 天数/工时
  amount?: number;     // 任务金额
  amountType?: TaskAmountType;  // 金额类型
  location?: string;   // 任务地点
  workMode?: TaskWorkMode; // 工作方式
  attachmentFiles?: string;
  attachments?: TaskAttachment[];  // 附件
  taskUsers?: TaskUser[];  // 任务人（多选）
  // 子任务列表
  subTasks?: SubTask[];
  // 关联字段
  companyId?: string;
  companyName?: string;
  contactId?: string;
  contactName?: string;
  projectId?: string;
  projectName?: string;
  /** Optional project milestone / stage association. */
  milestoneId?: string;
  /** 来源项目类日程 ID */
  scheduleId?: string;
  /** 后端 crm_task.owner，与 sys_user / hr 员工 id 对齐 */
  ownerId?: string;
  owner: string;
  /** 看板等处的展示用客户名 */
  customerName?: string;
  createdAt: string;
  completedAt?: string;
}

export interface SalesStats {
  totalSales: number;
  monthlyGrowth: number;
  newCustomers: number;
  customerGrowth: number;
  pipelineValue: number;
  taskCompletionRate: number;
}

/** 与后端 HR EmployeeVO 对齐（列表/详情通用） */
export interface EmployeeVO {
  id: string;
  employeeNo?: string;
  name: string;
  gender?: string;
  phone?: string;
  email?: string;
  department?: string;
  position?: string;
  status?: string;
  avatar?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role: 'super_admin' | 'admin' | 'manager' | 'sales' | 'consultant';
  department: string;
  /** 人事档案 id（sys_user.hr_employee_id），商机负责人 Select 优先用此值 */
  hrEmployeeId?: string;
  /** 人事档案职位（员工接口），用于默认商机协作角色等 */
  position?: string;
  // 人天成本（顾问）
  dailyRate?: number;
}

export interface UserProfileSettings {
  name: string;
  email: string;
  department: string;
}

export interface UserNotificationSettings {
  /** 审批通知（工作流待审批/报销结果等 type=approval） */
  approval: boolean;
  /** 到期提醒（到期/逾期 type=due/overdue） */
  dueReminder: boolean;
  /** 其他通知（任务指派/催办、商机关注、系统通知等 type=system） */
  system: boolean;
  /** 邮件站外通知（须服务器开启 SMTP；关闭后仍可有铃铛/企微） */
  email?: boolean;
  /** 账号安全邮件：开通账号、密码重置等 */
  emailAccountSecurity?: boolean;
  /** 审批结果邮件：合同/费用等审批通过或驳回 */
  emailApprovalResult?: boolean;
  /** 超时未审批提醒邮件 */
  emailApprovalOverdue?: boolean;
  /** 财务风险邮件：应收到期、逾期等 */
  emailFinanceRisk?: boolean;
}

export interface UserSystemSettings {
  darkMode: boolean;
  autoSave: boolean;
  showCompletedTasks: boolean;
}

/** 未登录或未初始化时的占位用户，避免全站使用 `User | null`。是否已登录请结合 token 等判断。 */
export const EMPTY_USER: User = {
  id: '',
  name: '',
  email: '',
  avatar: '',
  role: 'sales',
  department: '',
};

export interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'note' | 'visit';
  title: string;
  description: string;
  companyId: string;
  companyName: string;
  contactId?: string;
  contactName?: string;
  userId: string;
  userName: string;
  createdAt: string;
}

// 日程类型
export type ScheduleCategory = 'daily' | 'market' | 'client' | 'project' | 'opportunity';

// 日程状态
export type ScheduleStatus = 'scheduled' | 'completed' | 'cancelled';

// 提醒时间选项（分钟）
export type ReminderOption = 0 | 5 | 10 | 15 | 30 | 60 | 120 | 1440;

// 参与人
export interface ScheduleParticipant {
  id: string;
  name: string;
  // 人天成本（可覆盖用户的默认人天成本）
  dailyRate?: number;
  // 是否为外部联系人
  isExternal?: boolean;
  phone?: string;
  email?: string;
}

// 日程
export interface Schedule {
  id: string;
  title: string;
  description?: string;
  // 后端 type 字段映射到 category (meeting->daily, event->market, demo->opportunity, project->project)
  type?: string;
  category: ScheduleCategory;
  /** 小类（副标题）业务键，与 SCHEDULE_SUBTYPE_OPTIONS 对应；筛选仍用 category */
  scheduleSubtype?: string;
  startTime: string;
  endTime: string;
  location?: string;
  // 日程状态
  status?: ScheduleStatus;
  // 完成信息
  completedAt?: string;
  completionNotes?: string;
  // 提醒设置
  reminder?: {
    enabled: boolean;
    minutes: ReminderOption;
  };
  // 参与人
  participants?: ScheduleParticipant[];
  // 客户相关
  customerId?: string;
  customerName?: string;
  opportunityId?: string;
  opportunityName?: string;
  // 项目相关
  companyId?: string;
  companyName?: string;
  projectId?: string;
  projectName?: string;
  /** 关联任务 ID（项目类日程） */
  linkedTaskId?: string;
  // 联系人相关（商机日程）
  contactIds?: string[];
  contactNames?: string[];
  /** 客户经理（sys 用户 id），可与任务人/参与人不是同一人 */
  owner: string;
  /** 负责人用户 ID，便于编辑回填与接口兼容 */
  ownerId?: string;
  /** 负责人展示名 */
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
  // 跨天日程标记
  isMultiDay?: boolean;
  // 人力成本相关
  manDays?: number;        // 人天数（总工时/8）
  totalCost?: number;       // 人力成本总额（系统自动计算）
  isBillable?: boolean;     // 是否计入项目成本
  /** 省市区（外勤/日程表单） */
  province?: string;
  city?: string;
  /** 项目日程：与任务模块「任务人列表」一致，用于编辑回显及生成关联任务 */
  projectTaskAssignees?: {
    userId: string;
    userName?: string;
    roleType: string;
    roleTypeName?: string;
    startDate: string;
    endDate: string;
    amount?: number;
  }[];
  /** 前端展示用分类配置（按 category 键） */
  categoryConfig?: Partial<Record<ScheduleCategory, { name?: string; color?: string }>>;
  /** 与 startTime 并存时的日期字段 */
  startDate?: string;
}

// ==================== 财务管理模块类型定义 ====================

// 收款方式
export type PaymentMethod = 'one_time' | 'installment' | 'milestone';

// 发票类型
export type InvoiceType = 'special' | 'normal';  // 专票 / 普票

// 发票状态
export type InvoiceStatus = 'draft' | 'pending' | 'issued' | 'claimed' | 'voided';  // 草稿/待开 / 已开 / 已领取 / 红冲

// 报销审批状态
export type ExpenseStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'paid';

// 应收款状态
export type ReceivableStatus = 'unpaid' | 'partial' | 'paid' | 'overdue';

// 合同（与 FinContract + 页面表单字段合并）
export interface Contract {
  id: string;
  version?: number;
  contractNo: string;          // 合同编号
  contractName: string;         // 合同名称（展示）
  /** 后端 FinContract.name */
  name?: string;
  /** 后端 FinContract.amount */
  amount?: number;
  /** 后端 signedDate */
  signedDate?: string;
  companyId: string;            // 客户ID
  companyName: string;          // 客户名称
  projectId?: string;           // 关联项目ID
  projectName?: string;         // 关联项目名称
  signDate: string;             // 签约日期
  startDate: string;            // 开始日期
  endDate: string;              // 结束日期
  totalAmount: number;          // 合同总金额（含税）
  taxRate: number;              // 税率
  taxAmount: number;             // 税额
  netAmount: number;            // 不含税金额
  paymentMethod: PaymentMethod; // 收款方式
  status: 'draft' | 'active' | 'completed' | 'terminated';  // 合同状态
  attachments?: TaskAttachment[]; // 合同附件
  owner: string;                // 负责人
  remark?: string;              // 备注
  /** 后端 createTime */
  createTime?: string;
  /** 后端 updateTime */
  updateTime?: string;
  createdAt: string;
  updatedAt: string;
}

// 催收记录
export interface CollectionRecord {
  id: string;
  receivablePlanId: string;      // 关联应收计划ID
  collectionDate: string;        // 催收日期
  collectionType: 'phone' | 'email' | 'visit' | 'letter' | 'legal';  // 催收方式
  collector: string;              // 催收人
  amount: number;                // 本次催收金额（部分催收时）
  result: 'promise' | 'refused' | 'negotiating' | 'resolved';  // 催收结果
  nextAction?: string;           // 下一步行动
  nextFollowDate?: string;       // 下次跟进日期
  remark?: string;               // 备注
  createdAt: string;
}

// 应收款计划
export interface ReceivablePlan {
  id: string;
  version?: number;
  remark?: string;
  contractId: string;           // 关联合同ID
  contractName: string;         // 合同名称
  projectId?: string;           // 关联项目ID
  projectName?: string;         // 项目名称
  companyName: string;          // 客户名称
  companyId?: string;           // 客户ID（穿透/关联展示）
  period: number;               // 期次
  plannedAmount: number;        // 计划收款金额（含税）
  plannedDate: string;          // 计划收款日期
  receivedAmount: number;       // 已收款金额
  unpaidAmount: number;         // 未收款金额
  status: ReceivableStatus;     // 状态
  overdueDays?: number;         // 逾期天数
  // 催收记录
  collectionRecords?: CollectionRecord[];
  // 最后催收信息
  lastCollectionDate?: string;
  lastCollectionResult?: string;
  // 预警标记
  warningLevel?: 'normal' | 'attention' | 'warning' | 'critical';
  createdAt: string;
  updatedAt: string;
}

// 回款记录
export interface PaymentRecord {
  id: string;
  /** 客户回款时必填；应付款侧付款记录可为空 */
  contractId?: string;           // 关联合同ID
  contractName?: string;         // 合同名称
  companyId?: string;           // 客户ID
  companyName?: string;          // 客户名称
  /** 与应付款付款记录混列表时用于供应商匹配 */
  vendorName?: string;
  receivablePlanId?: string;    // 关联应收计划ID
  projectId?: string;
  projectName?: string;
  marketingActivityId?: string;
  marketingActivityName?: string;
  amount: number;               // 回款金额
  paymentDate: string;          // 到账日期
  paymentMethod: 'bank' | 'cash' | 'alipay' | 'wechat' | 'other';  // 付款方式
  bankSerialNo?: string;        // 银行流水号
  operator: string;             // 操作人
  /** 兼容看板/流水展示 */
  paidBy?: string;
  /** 与应付款付款记录字段对齐时的可选编号/类型 */
  paymentNo?: string;
  paymentType?: string;
  payableId?: string;
  payableNo?: string;
  remark?: string;             // 备注
  createdAt: string;
}

// 发票
export interface Invoice {
  id: string;
  version?: number;
  invoiceNo: string;            // 发票号码
  contractId?: string;          // 关联合同ID
  contractName?: string;        // 合同名称
  projectId?: string;           // 关联项目ID
  projectName?: string;         // 项目名称
  marketingActivityId?: string;
  marketingActivityName?: string;
  companyId?: string;           // 客户ID
  companyName: string;          // 客户名称
  invoiceTargetType?: 'enterprise' | 'person';
  invoiceType: InvoiceType;     // 发票类型
  taxNo?: string;
  address?: string;
  phone?: string;
  bankName?: string;
  bankAccount?: string;
  receivableId?: string;
  taxRate: number;              // 税率
  amountWithoutTax: number;      // 不含税金额
  taxAmount: number;            // 税额
  totalAmount: number;          // 含税总金额
  createTime?: string;
  issuedDate: string;           // 开票日期
  status: InvoiceStatus;        // 状态
  // 领取信息（适用于电子发票）
  claimedDate?: string;          // 领取日期
  claimedBy?: string;            // 领取人
  // 红冲信息
  voidReason?: string;          // 红冲原因
  voidDate?: string;            // 红冲日期
  voidedBy?: string;            // 红冲操作人
  remark?: string;              // 备注
  applicant: string;            // 申请人
  createdBy?: string;
  files?: string;
  sourceType?: string;
  sourceId?: string;
  createdAt: string;
  updatedAt: string;
}

// 报销分类
export type ExpenseCategory = 'daily' | 'marketing' | 'opportunity' | 'project';

// 报销费用类型（统一列表）
export type ExpenseType =
  // 差旅类
  | 'transport' | 'accommodation'
  // 餐费类
  | 'meals'
  // 市场类
  | 'marketing_activity' | 'advertising' | 'promotional' | 'exhibition' | 'market_research' | 'brand'
  // 招待类
  | 'gifts' | 'entertainment'
  // 项目类
  | 'subcontract' | 'project_material' | 'project_other'
  // 日常类
  | 'office_supplies' | 'communication' | 'printing' | 'postage' | 'software' | 'training' | 'benefits';

// 费用报销
export interface Expense {
  id: string;
  version?: number;
  expenseNo: string;            // 报销单号
  applicant: string;            // 申请人
  department: string;            // 部门
  marketingActivityId?: string; // 关联市场活动ID
  projectId?: string;           // 归属项目ID
  projectName?: string;         // 归属项目名称
  companyId?: string;           // 关联客户ID
  companyName?: string;          // 关联客户名称
  category?: ExpenseCategory;  // 报销分类
  expenseType: ExpenseType;      // 报销费用类型
  expenseDate: string;          // 费用日期
  amount: number;               // 金额
  taxRate?: number;             // 税率
  taxAmount?: number;           // 税额
  totalAmount: number;           // 含税总额
  attachments?: TaskAttachment[]; // 发票照片
  status: ExpenseStatus;        // 审批状态
  submittedAt?: string;         // 提交时间（ISO 或 yyyy-MM-dd HH:mm:ss）
  approvedBy?: string;           // 业务审批人 ID
  approvedByName?: string;       // 业务审批人姓名
  approvedAt?: string;           // 审批时间
  paidBy?: string;               // 财务付款人 ID
  paidByName?: string;           // 财务付款人姓名
  paidAt?: string;               // 付款时间
  rejectReason?: string;         // 最近一次驳回原因
  rejectedAt?: string;           // 驳回时间
  awaitingPayment?: boolean;     // 是否停在「财务付款」节点、等待付款
  items?: string;                // 后端扩展明细 JSON
  // 历史兼容字段：费用报销不再维护借款冲抵，冲抵请在借款管理处理
  loanId?: string;
  loanOffsetAmount?: number;
  description?: string;
  opportunityName?: string;
  marketingActivityName?: string;
  remark?: string;              // 备注/事由
  /** 是否已附发票（详情展示） */
  invoiceAttached?: boolean;
  createdAt: string;
  updatedAt: string;
}

// 借款单
export interface Loan {
  id: string;
  version?: number;
  loanNo: string;              // 借款单号
  borrower: string;             // 借款人
  department: string;           // 部门
  amount: number;               // 借款金额
  usedAmount: number;           // 已冲抵金额
  remainingAmount?: number;      // 剩余未冲抵金额
  purpose: string;              // 借款用途
  /** 借款日期（列表/详情展示） */
  loanDate?: string;
  expectedRepayDate: string;   // 预计还款/冲抵日期
  status: 'pending' | 'partial' | 'settled';  // 状态
  remark?: string;              // 备注
  createdAt: string;
  updatedAt: string;
}

// 付款状态
export type PaymentStatus = 'pending' | 'partial' | 'completed' | 'overdue' | 'paid' | 'cancelled';

// 应付款（外包费用）
export interface Payable {
  id: string;
  version?: number;
  payableNo: string;            // 应付款单号
  contractId?: string;            // 关联合同（用于穿透项目）
  payableType: 'subcontract' | 'expert' | 'supplier' | 'other';  // 付款类型
  businessType?: 'project' | 'activity' | 'general';
  marketingActivityId?: string;
  marketingActivityName?: string;
  costCategory?: 'venue' | 'catering' | 'trainer' | 'material' | 'travel' | 'platform' | 'other';
  /** 供应商/外包方 ID（与 vendorName 并存） */
  vendorId?: string;
  vendorName: string;           // 供应商/专家名称
  /** 后端 API 原字段（列表映射前可能仅有此项） */
  supplierName?: string;
  projectId?: string;           // 归属项目ID
  projectName?: string;         // 归属项目名称
  contractContent: string;      // 合作内容
  amount: number;               // 应付金额（含税）
  taxRate: number;              // 税率
  taxAmount: number;            // 税额
  netAmount: number;            // 不含税金额
  invoiceReceived: boolean;      // 是否收到发票
  plannedPayDate?: string;      // 计划付款日期
  paidAmount: number;           // 已付款金额
  unpaidAmount: number;         // 未付款金额
  status: PaymentStatus;        // 状态
  paidDate?: string;            // 实际付款日期
  bankSerialNo?: string;        // 付款流水号
  applicant: string;            // 申请人
  remark?: string;              // 备注
  createdAt: string;
  updatedAt: string;
}

// 付款记录（应付款付款）
export interface PayablePaymentRecord {
  id: string;
  paymentNo: string;           // 付款单号
  payableId?: string;          // 关联应付款ID
  payableNo?: string;          // 关联应付款单号
  /** 关联应收计划（与回款穿透一致时的可选字段） */
  receivablePlanId?: string;
  paymentType: 'subcontract' | 'expert' | 'supplier' | 'expense' | 'reimbursement' | 'other';  // 付款类型
  vendorName: string;           // 收款方名称
  amount: number;              // 付款金额
  paymentMethod: 'bank' | 'cash' | 'alipay' | 'wechat';  // 付款方式
  paymentDate: string;         // 付款日期
  bankSerialNo?: string;        // 银行流水号
  projectId?: string;          // 关联项目ID
  projectName?: string;         // 关联项目名称
  expenseId?: string;          // 关联报销单ID
  operator: string;             // 操作人
  remark?: string;              // 备注
  createdAt: string;
  /** 关联合同（看板/穿透展示用） */
  contractId?: string;
  companyName?: string;
  paidBy?: string;
}

// 资金流水
export interface FundRecord {
  id: string;
  recordNo: string;             // 流水号
  type: 'income' | 'expense';   // 收支类型
  amount: number;               // 金额
  counterparty: string;         // 对方单位
  accountDate: string;          // 账务日期
  paymentMethod: 'bank' | 'cash' | 'alipay' | 'wechat' | 'other';  // 收付款方式
  bankSerialNo?: string;        // 银行流水号
  contractId?: string;          // 关联合同ID
  contractName?: string;        // 合同名称
  projectId?: string;           // 关联项目ID
  projectName?: string;         // 项目名称
  expenseId?: string;           // 关联报销单ID
  payableId?: string;           // 关联应付款ID
  remark?: string;              // 备注
  operator: string;             // 操作人
  createdAt: string;
}

// 财务配置
export interface FinanceConfig {
  id: string;
  /** 本位币（门户配置） */
  currency?: string;
  // 收支类别
  incomeCategories: string[];    // 收入类型：咨询费、方案费、培训费、调研费、其他
  expenseCategories: string[];   // 支出类型：分包/专家费、差旅费、交通费、住宿费、餐费、办公费、市场费、税费、其他
  // 税率配置
  defaultTaxRate: number;        // 默认增值税率
  // 公司账户
  companyAccounts: {
    name: string;               // 账户名称
    bank: string;              // 开户行
    accountNo: string;         // 账号
    remark?: string;           // 备注
  }[];
  // 客户财务信息
  companyFinanceInfo: {
    companyId: string;
    taxNo: string;              // 税号
    address: string;            // 地址电话
    bank: string;              // 开户行
    accountNo: string;         // 账号
    invoiceType: InvoiceType;  // 默认开票类型
    creditPeriod: number;       // 信用期（天）
    reminderDays: number;       // 账期提醒天数
  }[];
  updatedAt: string;
}

// 项目财务概览（计算得出，不存储）
export interface ProjectFinanceOverview {
  projectId: string;
  projectName: string;
  companyName: string;
  companyId?: string;
  /** 与 Project.status 对齐，列表筛选用 */
  projectStatus?: Project['status'];
  contractAmount: number;       // 合同总额
  implementationAmount?: number; // 实施额（任务汇总）
  invoicedAmount: number;       // 已开票金额
  receivedAmount: number;       // 已回款金额
  receivableAmount?: number;    // 应收款（实施额-已回款）
  pendingAmount: number;        // 待回款金额
  totalCost: number;            // 项目总成本
  expenseCost?: number;         // 报销成本（差旅+其他）
  payableCost?: number;         // 外包/应付款成本
  laborCost?: number;           // 人力成本
  subcontractCost?: number;      // 分包/专家成本
  travelCost?: number;           // 差旅成本
  otherCost?: number;            // 其他直接成本
  profit: number;               // 项目毛利
  profitRate: number;           // 毛利率
}

// 财务报表统计
export interface FinanceReport {
  // 应收账款统计
  totalReceivable: number;      // 应收总额
  overdueReceivable: number;    // 逾期应收
  // 应收账龄
  receivablesByAge: {
    '0-30': number;             // 0-30天
    '31-60': number;           // 31-60天
    '61-90': number;           // 61-90天
    '90+': number;             // 90天以上
  };
  // 本月经营数据
  monthlyIncome: number;        // 本月收入
  monthlyReceived: number;       // 本月回款
  monthlyExpense: number;       // 本月支出
  monthlyCost: number;         // 本月成本
  monthlyProfit: number;        // 本月利润
  // 费用结构
  expensesByType: Record<string, number>;  // 按类型统计费用
  expensesByProject: Record<string, number>;  // 按项目统计费用
}

// ==================== 知识管理模块类型定义 ====================

// 知识类型
export type KnowledgeType = 'article' | 'video' | 'audio' | 'document' | 'report' | 'method';

// 知识来源
export type KnowledgeSource = 'internal' | 'wechat' | 'external' | 'project';

// 知识状态
export type KnowledgeStatus = 'draft' | 'published' | 'archived';

// 知识分类 - 支持树形结构
export interface KnowledgeCategory {
  id: string;
  name: string;              // 分类名称
  parentId?: string;        // 父分类ID（一级分类为空）
  level: number;             // 层级深度（1=一级, 2=二级, 3=三级）
  order: number;            // 排序
  description?: string;     // 分类描述
  icon?: string;            // 图标
  color?: string;           // 颜色
  knowledgeCount?: number;  // 知识数量
  // 权限控制
  accessLevel?: 'public' | 'internal' | 'restricted' | 'confidential';
  allowedRoles?: string[];   // 允许访问的角色
  // 分类类型
  categoryType: 'ingest' | 'core' | 'industry' | 'project' | 'compliance' | 'operation';
}

// 知识附件
export interface KnowledgeAttachment {
  id: string;
  name: string;             // 文件名
  url: string;               // 文件URL
  size: number;              // 文件大小
  type: string;              // 文件类型
  format?: string;           // 文件格式 (pdf, doc, mp4等)
  indexStatus?: 'ready' | 'indexing' | 'failed' | 'unsupported';
  indexError?: string;
  chunkCount?: number;
}

// 知识条目
export interface Knowledge {
  id: string;
  title: string;             // 标题
  summary?: string;          // 摘要/简介
  content?: string;           // 正文内容（富文本/HTML）
  type: KnowledgeType;        // 知识类型
  source: KnowledgeSource;     // 来源
  sourceUrl?: string;         // 原文链接（公众号等）
  coverImage?: string;        // 封面图
  attachments?: KnowledgeAttachment[];  // 附件列表
  tags: string[];             // 标签
  categoryIds: string[];      // 分类ID列表

  // 多维度标签
  industry?: string;          // 行业标签
  module?: string;            // 咨询模块（战略/组织/人力/流程/数字化/文化）
  projectStage?: string;      // 项目阶段（前期/中期/后期）

  // 关联信息
  relatedProjectId?: string;  // 关联项目ID
  relatedProjectName?: string;  // 关联项目名称
  // 公众号特有字段
  wechatAuthor?: string;      // 公众号作者
  wechatPublishTime?: string;  // 公众号发布时间
  wechatDigest?: string;      // 公众号摘要
  // 统计信息
  viewCount: number;          // 浏览次数
  likeCount: number;          // 点赞次数
  collectCount: number;       // 收藏次数
  commentCount?: number;       // 评论次数
  // 权限信息
  accessLevel: 'public' | 'internal' | 'restricted' | 'confidential';  // 访问级别
  allowedRoles?: string[];     // 允许访问的角色
  // 状态
  status: KnowledgeStatus;     // 状态
  author: string;             // 作者/创建人
  authorId: string;           // 作者ID
  createTime?: string;        // Backend create time
  createdAt: string;          // 创建时间
  updatedAt: string;          // 更新时间
  publishedAt?: string;       // 发布时间
  version?: string;           // 版本号
  // 审核信息
  reviewStatus?: 'pending' | 'approved' | 'rejected';
  reviewComment?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

// 微信公众号同步配置
export interface WechatSyncConfig {
  id: string;
  name: string;               // 配置名称
  appId: string;              // 公众号AppID
  appSecret: string;          // 公众号AppSecret
  accountType: 'subscription' | 'service';  // 公众号类型
  syncEnabled: boolean;       // 是否启用同步
  syncFrequency: 'daily' | 'weekly' | 'manual';  // 同步频率
  lastSyncTime?: string;      // 最后同步时间
  autoPublish: boolean;       // 是否自动发布
  defaultCategory?: string;   // 默认分类
  status: 'active' | 'inactive' | 'error';
  errorMessage?: string;      // 错误信息
  createdAt: string;
  updatedAt: string;
}

// 微信公众号同步记录
export interface WechatSyncRecord {
  id: string;
  configId: string;          // 关联配置ID
  syncTime: string;           // 同步时间
  itemsSynced: number;        // 同步条目数
  articlesCount: number;      // 图文数量
  videosCount: number;        // 视频数量
  audiosCount: number;        // 音频数量
  lastArticleId?: string;     // 最后同步的文章ID
  status: 'success' | 'failed' | 'partial';
  errorMessage?: string;      // 错误信息
  duration: number;           // 同步耗时（秒）
}

// 公众号文章
export interface WechatArticle {
  id: string;
  configId: string;          // 同步配置ID
  mediaId: string;           // 微信素材ID
  title: string;             // 文章标题
  author?: string;           // 作者
  digest?: string;           // 摘要
  coverUrl?: string;          // 封面图片
  contentUrl: string;        // 原文链接
  thumbMediaId?: string;      // 缩略图ID
  showCoverPic?: number;      // 是否显示封面
  createdAt: string;         // 创建时间
  updateTime?: string;        // 更新时间
  syncRecordId?: string;     // 同步记录ID
}

// 知识收藏
export interface KnowledgeFavorite {
  id: string;
  knowledgeId: string;       // 知识ID
  userId: string;            // 用户ID
  userName: string;          // 用户名
  createdAt: string;         // 收藏时间
}

// 知识点赞
export interface KnowledgeLike {
  id: string;
  knowledgeId: string;       // 知识ID
  userId: string;            // 用户ID
  userName: string;          // 用户名
  createdAt: string;         // 点赞时间
}

// 知识评论
export interface KnowledgeComment {
  id: string;
  knowledgeId: string;       // 知识ID
  userId: string;            // 评论用户ID
  userName: string;          // 评论用户名
  userAvatar?: string;       // 用户头像
  content: string;           // 评论内容
  parentId?: string;         // 父评论ID（回复）
  likes: number;              // 回复数
  createdAt: string;         // 评论时间
  updatedAt?: string;        // 更新时间
}

// 知识访问记录
export interface KnowledgeView {
  id: string;
  knowledgeId: string;       // 知识ID
  userId: string;            // 用户ID
  userName: string;          // 用户名
  viewedAt: string;          // 访问时间
  duration?: number;         // 停留时长（秒）
}

// ==================== 企业月结费用模块类型定义 ====================

// 月结服务商类型
export type MonthlyExpenseProvider = 'ctrip' | 'didi' | 'express' | 'printing' | 'other';

// 月结费用状态
export type MonthlyExpenseStatus = 'pending' | 'confirmed';

// 月结费用归属类型（与报销分类一致）
export type MonthlyExpenseCategory = 'daily' | 'marketing' | 'opportunity' | 'project';

// 月结费用费用类型（对应报销的报销名目）
export type MonthlyExpenseType = 'transport' | 'accommodation' | 'train' | 'taxi' | 'express' | 'printing';

// 月结费用记录（从第三方平台导入的费用）
export interface MonthlyExpenseRecord {
  id: string;
  version?: number;
  recordNo: string;                    // 记录编号
  provider: MonthlyExpenseProvider;    // 服务商
  providerName: string;               // 服务商名称（中文）
  orderNo: string;                     // 第三方订单号
  // 统一处理人字段（携程=预订人，滴滴=使用人，快递=寄件人，打印=使用人）
  handler: string;                     // 处理人
  handlerName?: string;               // 处理人姓名
  // 携程特有字段
  booker?: string;                    // 预订人（携程）
  user?: string;                      // 使用人（携程/滴滴/打印）
  bookDate?: string;                  // 预订日期（携程）
  startDate?: string;                 // 开始日期
  endDate?: string;                   // 结束日期
  origin?: string;                    // 出发地/起点（携程/滴滴）
  destination?: string;              // 目的地（携程/滴滴/快递）
  // 滴滴特有字段
  rideDate?: string;                 // 用车日期（滴滴）
  // 快递特有字段
  sender?: string;                    // 寄件人（快递）
  receiver?: string;                  // 收件人（快递）
  weight?: string;                    // 重量（快递）
  sendDate?: string;                  // 寄件日期（快递）
  // 打印特有字段
  content?: string;                   // 打印内容（打印）
  copies?: number;                    // 份数（打印）
  applyDate?: string;                 // 申请日期（打印）
  // 通用字段
  expenseType: MonthlyExpenseType;    // 费用类型（机票费/住宿费/火车票/打车费/快递费/打印费）
  amount: number;                     // 金额
  category?: MonthlyExpenseCategory;   // 归属类型
  projectId?: string;                 // 归属项目ID
  projectName?: string;               // 归属项目名称
  opportunityId?: string;             // 归属商机ID
  opportunityName?: string;           // 归属商机名称
  marketingActivityId?: string;       // 归属市场活动ID
  marketingActivityName?: string;     // 归属市场活动名称
  companyId?: string;                // 关联客户ID
  companyName?: string;               // 关联客户名称
  status: MonthlyExpenseStatus;       // 状态
  confirmBy?: string;                 // 确认人
  confirmAt?: string;                 // 确认时间
  remark?: string;                    // 备注
  createdAt: string;                   // 创建时间（导入时间）
  updatedAt: string;                   // 更新时间
}

// 携程导入记录
export interface CtripImport {
  orderNo: string;        // 订单号
  booker: string;         // 预订人
  user: string;           // 使用人
  bookDate: string;       // 预订日期
  startDate: string;      // 开始日期
  endDate: string;        // 结束日期
  tripType: string;       // 行程类型：机票/酒店/高铁
  origin: string;         // 出发地
  destination: string;    // 目的地
  amount: number;         // 金额
}

// 滴滴导入记录
export interface DidiImport {
  orderNo: string;        // 订单号
  user: string;           // 使用人
  rideDate: string;      // 用车日期
  origin: string;         // 出发地
  destination: string;    // 目的地
  amount: number;         // 金额
}

// 快递导入记录
export interface ExpressImport {
  orderNo: string;        // 订单号
  sender: string;         // 寄件人
  receiver: string;       // 收件人
  sendDate: string;       // 寄件日期
  destination: string;    // 目的地
  weight: string;        // 重量
  amount: number;         // 金额
}

// 打印导入记录
export interface PrintingImport {
  orderNo: string;        // 订单号
  user: string;           // 使用人
  applyDate: string;      // 申请日期
  content: string;        // 打印内容
  copies: number;         // 份数
  amount: number;         // 金额
}

// ==================== 专家库管理模块类型定义 ====================

// 专家类型
export type ExpertType = 'consultant' | 'technical' | 'partner';

// 专家级别
export type ExpertLevel = 'senior' | 'mid' | 'junior';

// 专家状态
export type ExpertStatus = 'active' | 'inactive';

// 专家可用状态
export type ExpertAvailability = 'available' | 'busy' | 'unavailable';

// 专家
export interface Expert {
  id: string;
  version?: number;
  name: string;                    // 姓名
  gender?: string;                 // 性别
  phone?: string;                  // 手机号
  email?: string;                  // 邮箱
  company?: string;                // 所属公司
  position?: string;               // 职位
  expertise?: string;              // 专业领域（多选，逗号分隔）
  industry?: string;               // 行业领域（多选，逗号分隔）
  type?: ExpertType;               // 类型
  level?: ExpertLevel;             // 级别
  status: ExpertStatus;            // 状态
  hourlyRate?: number;             // 时薪
  dailyRate?: number;              // 日薪
  availability: ExpertAvailability; // 可用状态
  experience?: string;             // 从业经验
  certifications?: string;         // 资质证书
  projectsCompleted?: number;      // 已完成项目数
  rating?: number;                 // 评分（0-5）
  cooperationStart?: string;       // 合作开始日期
  cooperationEnd?: string;         // 合作结束日期
  remark?: string;                 // 备注
  owner?: string;                  // 负责人ID
  createTime?: string;             // 创建时间
  updateTime?: string;             // 更新时间
}

// 专家项目关联
export interface ExpertProject {
  id: string;
  expertId: string;                // 专家ID
  expertName: string;              // 专家姓名
  projectId: string;               // 项目ID
  projectName: string;             // 项目名称
  role: string;                    // 角色
  startDate: string;               // 开始日期
  endDate?: string;                // 结束日期
  workDays: number;                // 工作天数
  dailyRate: number;               // 日薪
  totalAmount: number;             // 总金额
  status: 'ongoing' | 'completed'; // 状态
  remark?: string;                 // 备注
  createdAt: string;
}

// 专家评价
export interface ExpertEvaluation {
  id: string;
  version?: number;
  expertId: string;                // 专家ID
  expertName: string;              // 专家姓名
  projectId?: string;              // 项目ID
  projectName?: string;            // 项目名称
  rating: number;                  // 评分（1-5）
  comment?: string;                // 评价内容
  evaluator: string;               // 评价人
  evaluatorId: string;             // 评价人ID
  createdAt: string;
}

// ==================== 供应商管理模块类型定义 ====================

// 供应商合作状态
export type SupplierStatus = 'active' | 'inactive' | 'pending' | 'blacklist';

// 供应商类型
export type SupplierType = 'logistics' | 'printing' | 'consulting' | 'training' | 'it' | 'office' | 'other';

// 供应商结算方式
export type SupplierSettlementType = 'monthly' | 'quarterly' | 'per_project' | 'one_time';

// 供应商
export interface Supplier {
  id: string;
  version?: number;
  supplierNo: string;                    // 供应商编号
  name: string;                         // 供应商名称
  shortName?: string;                    // 简称
  type: SupplierType;                    // 供应商类型
  category?: string;                      // 经营类目（如：机票、酒店、快递、打印等）
  // 联系信息
  contactPerson?: string;                // 联系人
  contactId?: string;                    // 联系人管理总表 ID
  contactPhone?: string;                 // 联系电话
  contactEmail?: string;                 // 联系邮箱
  address?: string;                      // 地址
  // 开票信息
  taxNo?: string;                        // 税号
  bankName?: string;                     // 开户银行
  bankAccount?: string;                  // 银行账号
  invoiceTitle?: string;                 // 发票抬头
  invoiceAddress?: string;               // 开票地址
  invoicePhone?: string;                  // 开票电话
  // 合作信息
  status: SupplierStatus;                // 合作状态
  cooperationStartDate?: string;         // 合作开始日期
  cooperationEndDate?: string;           // 合作结束日期
  settlementType?: SupplierSettlementType; // 结算方式
  creditPeriod?: number;                  // 信用期（天）
  // 累计交易
  totalTransactionAmount?: number;        // 累计交易金额
  totalTransactionCount?: number;        // 累计交易次数
  lastTransactionDate?: string;          // 最近交易日期
  // 附件
  attachments?: TaskAttachment[];        // 合同、资质证书等附件
  // 其他
  remark?: string;                       // 备注
  owner: string;                         // 负责人
  createdAt: string;
  updatedAt: string;
}

// ==================== 业务状态枚举 ====================

export type CompanyStatus = 'active' | 'inactive' | 'potential';
export type OpportunityStage = 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
export type ProjectStatus = 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost' | 'planning' | 'execution' | 'uat' | 'completed' | 'cancelled';
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'overdue';
export type Priority = 'high' | 'medium' | 'low';
export type UserRole = 'super_admin' | 'admin' | 'manager' | 'sales' | 'consultant';
export type ActivityType = 'call' | 'email' | 'meeting' | 'note' | 'visit';

export type { Result, PageResult, ApiResponse } from '../services/types';

export interface PaginationParams {
  page: number;
  pageSize: number;
  [key: string]: unknown;
}

export interface PaginatedData<T> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type PaginatedResponse<T> = import('../services/types').Result<PaginatedData<T>>;
